/* eslint-disable no-nested-ternary */
import * as R from 'ramda';
import { isAfter, isBefore, isSameDay, max } from 'date-fns';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { ApproveRequiredTypesEnum } from 'APIPackage/KnmModule/enums/ApproveRequiredTypesEnum';
import {
  getIsPlanErknmStatusApproved,
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusUnderConsideration,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  TIME_FORMAT,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import {
  FIELD_INCORRECTLY,
  INN_ERROR,
  KNM_SUBJECT_IS_EXIST_ERROR,
  KPP_ERROR,
  MAX_CHARACTERS_ERROR,
  MIN_CHARACTERS_ERROR,
  OGRN_LENGTH_ERROR,
  RAFP_NZA_ERROR,
  REQUIRED_ERROR,
  UNALLOWED_CHARACTERS_ERROR,
} from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { makeIsOgrn } from 'CorePackage/MainModule/validators/isOgrn';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { isStringDigitsOnly } from 'CorePackage/MainModule/validators/isStringDigitsOnly';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { makeIsErknmInn } from 'CorePackage/MainModule/validators/makeIsErknmInn';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { makeMaxNumber } from 'CorePackage/MainModule/validators/maxNumber';
import { makeIsNotEqualNumber } from 'CorePackage/MainModule/validators/isNotEqualNumber';
import { makeSnilsValidation } from 'CorePackage/MainModule/validators/isValidSnils';
import { IKnmFormDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmFormDocument';
import { KnmStatusesEnum } from 'KnmPackage/MainModule/enums/KnmStatusesEnum';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { KnmFormValidator } from 'KnmPackage/MainModule/types/KnmFormValidator';
import { PLAN_ID_LENGTH } from 'SmevMonitoringPackage/MainModule/constants/planIdLength';
import { isAssignmentRequiredByType } from 'KnmPackage/MainModule/common/isAssignmentRequiredByType';
import { store } from 'CorePackage/MainModule/common/store';
import { ErknmReasonsDocumentTypesEnum } from 'KnmErknmPackage/ErknmReasonsModule/enums/ErknmReasonsDocumentTypesEnum';
import { isPassportReasonLinkRequired } from 'KnmPackage/MainModule/common/isPassportReasonLinkRequired';
import { ErknmApproveRemarkSectionCodeEnum } from 'KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { NEW_REQUIREMENT_SELECT_OPTION_CODE } from '../constants/newRequirementSelectOption';
import { IApprovedSelectOption } from '../interfaces/IApprovedSelectOption';
import { IKnmErknmFormApproveDoc } from '../interfaces/IKnmErknmFormApproveDoc';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormExpert } from '../interfaces/IKnmErknmFormExpert';
import { IKnmErknmFormReason } from '../interfaces/IKnmErknmFormReason';
import { IKnmErknmFormRequirement } from '../interfaces/IKnmErknmFormRequirement';
import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IKnmErknmFormRequirementNpa } from '../interfaces/IKnmErknmFormRequirementNpa';
import { isDigitsCountCorrect } from '../../../CorePackage/MainModule/validators/isDigitsCountCorrect';
import { validateRafpNzaStatus } from '../../../CorePackage/MainModule/validators/validateRafpNzaStatus';
import { KnmSubjectTypesEnum } from '../../ErknmSubjectModule/components/ErknmSubjectInfo';
import { IKnmErknmFormOrganizationAdd } from '../interfaces/IKnmErknmFormOrganizationAdd';
import { formatDate, parseDate } from '../../../CorePackage/MainModule/common/formatDate';
import { IKnmErknmFormSuspensionEvent } from '../interfaces/IKnmErknmFormSuspensionEvent';
import { IKnmErknmFormChecklist } from '../interfaces/IKnmErknmFormChecklist';
import { IKnmErknmFormChecklistQuestion } from '../interfaces/IKnmErknmFormChecklistQuestion';
import { makeMinCharactersValidator } from '../../../CorePackage/MainModule/validators/minCharacters';
import { getUserRoleIsProsecutorOrSuperProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { newPlanSelectOption } from '../constants/newPlanSelectOption';
import { getTargetEntitySelectOptions } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { isKnmSubjectExist } from '../../../CorePackage/MainModule/validators/isKnmSubjectExist';
import { KnmErknmStatusCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { IKnmErknmFormRescheduling } from '../interfaces/IKnmErknmFormRescheduling';
import { IKnmNotificationsForm } from '../../../KnmPackage/KnmNotificationModule/interfaces/IKnmNotificationsForm';
import { getJudgmentsValidate } from './getJudmentsValidate';
import { getInspectorsErknmValidate } from './getInspectorsErknmValidate';
import { getDocumentsValidate } from './getDocumentsValidate';
import { getObjectsErknmValidate } from './getObjectsErknmValidate';
import { getArrayError } from '../../../CorePackage/MainModule/common/getArrayError';
import { makeInnChecksumVerification } from '../../../CorePackage/MainModule/validators/isInnCorrect';
import { isOneReasonLinkRequired } from '../../../KnmPackage/MainModule/common/isOneReasonLinkRequired';
import { IKnmErknmFormResultDecision } from '../interfaces/IKnmErknmFormResultDecision';
import { CHECKING_IT_ACCREDITATION } from '../../../CorePackage/MainModule/constants/notices';
import { getRiskIndicatorsValidate } from '../../../KnmPackage/MainModule/common/getRiskIndicatorsValidate';
import { getKnoSupervisionHazardClassApplies } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { NOT_IN_FIAS_TEXT } from '../../../CorePackage/MainModule/constants/notInFIASText';
import { validateInvalidFields } from './validateInvalidFields';
import { mergeDeepRightWithArray } from '../../../CorePackage/MainModule/common/mergeDeepRightWithArray';
import { FnsCheckStatusesEnum } from '../../ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { IKnmErknmFormPlace } from '../interfaces/IKnmErknmFormPlace';
import { getErknmActsValidate } from './getErknmActsValidate';
import { getErknmEventValidate } from './getErknmEventValidate';
import { getErknmResultDecisionsValidate } from './getErknmResultDecisionsValidate';
import { isErknmOrganizationNameFieldEnabledWithoutFiz } from './isErknmOrganizationNameFieldEnabledWithoutFiz';
import { makeRequiredQuillEditorDelta } from '../../../CorePackage/MainModule/validators/isRequiredQuillEditorDelta';
import { getErknmAgreementValidate } from './getErknmAgreementValidate';
import { IErknmFormAgreement } from '../interfaces/IErknmFormAgreement';
import { getViolationEvidencesValidator } from './violationEvidencesValidator';
import { IKnmErknmFormChangePlanReason } from '../interfaces/IKnmErknmFormChangePlanReason';
import { videoCallInfoValidator } from './videoCallInfoValidator';
import { cloneFormErrors } from '../../../CorePackage/MainModule/common/cloneFormErrors';

// проверить нужно ли вообще переносить условия, кажется, что если нет поля => нет валидации и все корректно
export const knmErknmValidator = ({
  values,
  planFindStatus,
  isSupervisor,
  isProsecutor,
  isWatchOnly,
  isNewKnm,
  isCopyEnabled,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isKnmStatusApproving,
  isKnmStatusTypeWaitingCarryOut,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusTypeDecisionAppealed,
  isKnmStatusTypeCantBeCarryOut,
  isKnmStatusCommentsExist,
  isErknmStatusAgreementOnApproval,
  knmPlanStatus,
  knmDataApprovedType,
  knmDataNote,
  settings,
  isEditableInStatusCommentsExist,
  knoSupervisionStartDate,
  isStartDateIsCurrentYear,
  excludeDates,
  isErknmStatusRejected,
  punishmentNature,
  initialErknmValues,
  fnsRegistryOkvedCodes,
  isPassportByCheckPrescriptionOldRules,
  isPassportByCheckPrescriptionFailed,
}: KnmFormValidator & { values: IKnmForm }): FormErrors<IKnmForm> => {
  const {
    knoOrganizationErknm,
    kindControl,
    kindKnm,
    typeErknm,
    planId,
    planGuid,
    oldPlanNumber,
    oldKnmNumber,
    startDate,
    startTime,
    stopDate,
    stopTime,
    durationDays,
    durationHours,
    directDurationDays,
    approveRequired,
    reasonsErknm,
    reasonDocuments,
    prosecutorOrganizationErknm,
    approveNote,
    approved,
    approveNoteAppeal,
    rejectReasonType,
    organizations,
    objectsErknm,
    inspectorsErknm,
    experts,
    isRemote,
    interactionCategory,
    remoteNote,
    eventsErknm,
    requirementsErknm,
    requirementsNpaErknm,
    approveDocs,
    places,
    organizationDocuments,
    acts,
    violatedRequirements,
    resultDecisions,
    judgments,
    documents,
    created,
    planSelectOption,
    suspensionEvents,
    isChecklistsUsed,
    checklistsErknm,
    approveNumberDecision,
    approveFioSigner,
    approveTitleSigner,
    approveDateDecision,
    reasonRequirements,
    notInFIAS: isNotInFIAS,
    manualAddress,
    region,
    isErrorFiasValue,
    shouldValidationForGeneratedDecision,
    placeDecision,
    titleSigner,
    fioSigner,
    abrogation,
    rescheduling,
    subjectNotifications,
    docTarget,
    approveFile,
    webDocuments,
    decisionDisabled: isDecisionDisabled,
    isHasCollaboratingOrganization,
    collaboratingOrganizationsErknm,
    changePlanReason,
    approveRemarks,
    agreements,
    videoCallInfo,
  } = values;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wrapArrayError = (errors: any, key: string) => {
    if (errors && typeof errors[key] === 'string') {
      // eslint-disable-next-line no-param-reassign
      errors[key] = getArrayError(errors[key]);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wrapNestedArrayError = (list: any, key: string) => {
    if (Array.isArray(list)) {
      list.forEach(item => wrapArrayError(item, key));
    }
  };

  const state = store.getState();
  const isOn411 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.COLLABORATION_KNO_ERVK_SEARCH_4_1_1);
  const isLaterThanFieldsMarksSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.FIELDS_MARKS_481_4_1_14,
  );
  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );
  const isShow4127 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_27_STARTS_FROM,
  );
  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );
  const isNeedToValidatePlacesRegion = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATE_PLACES_REGION,
  );
  const isEnableDateValidations = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.DATE_VALIDATION_4_1_34_STARTS_FROM,
  );

  const isProsecutorOrSuperProsecutor = getUserRoleIsProsecutorOrSuperProsecutor(state);
  const isHazardClassApplies = getKnoSupervisionHazardClassApplies(state);

  const isKindCodeDP = kindKnm?.code === KnmKindCodesEnum.DP;
  const isKindCodeVP = kindKnm?.code === KnmKindCodesEnum.VP;
  const isKindCodeRO = kindKnm?.code === KnmKindCodesEnum.RO;
  const isTypeCodePP = typeErknm?.code === KnmTypeCodesEnum.PP;
  const isTypeCodeVP = typeErknm?.code === KnmTypeCodesEnum.VP;
  const isPlanType = typeErknm?.code ? isTypeCodePP : false;
  const isTypeCodePP248P294 = typeErknm?.code === KnmTypeCodesEnum.PP248P294;
  const isReasonsNotEmpty = reasonsErknm ? reasonsErknm.length > 0 : false;
  const isUnscheduledType = typeErknm?.code ? isTypeCodeVP || isTypeCodePP : false;
  const reasonErknmDateBegin = reasonsErknm?.[0]?.type?.dateBegin;
  const reasonErknmDateEnd = reasonsErknm?.[0]?.type?.dateEnd;
  const isApproveRequired = approveRequired?.value === ApproveRequiredTypesEnum.REQUIRED;
  const isApprovedRequired4127 = isProsecutorOrSuperProsecutor && isShow4127;
  const isKnmPlanStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const isKnmPlanStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(knmPlanStatus);
  const isKnmPlanStatusApproved: boolean = getIsPlanErknmStatusApproved(knmPlanStatus);
  const subjectTypeOptions = getTargetEntitySelectOptions(state);
  const isKnmMandatoryDates = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_MANDATORY_DATES_STARTS_FROM,
  );
  const isShowCheckingProgram = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_41_STARTS_FROM,
  );
  const isValidateRequiredViolatedMandatory = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_43_STARTS_FROM,
  );
  const isUseKnoSupervisorStartDate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_36_STARTS_FROM,
  );
  const isUse4123 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_23_STARTS_FROM);
  const isUse2234114 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_14_STARTS_FROM,
  );
  const isViolationsStartDate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_29_STARTS_FROM,
  );

  const isShow4119ErvkCheckLists = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_19_ERVK_CHECK_LISTS_STARTS_FROM,
  );

  const isLaterThanReasonFieldsAvailabilitySetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.REASON_FIELDS_AVAILABILITY_4_1_54_STARTS_FROM,
  );

  const isValidateReasonAttachments =
    !isKnmStatusTypeWaitingCarryOut &&
    !isKnmStatusTypeCarriedOut &&
    !isKnmStatusTypeCompleted &&
    !isKnmStatusTypeDecisionAppealed &&
    !isKnmStatusTypeCantBeCarryOut &&
    !isKnmStatusApproving;

  const dateIn30WorkingDays = formatDate({
    date: excludeDates?.dateIn30WorkingDays || '',
    dateFormat: DATE_FORMAT_LONG_FNS,
  });

  const startExcludedDates = excludeDates?.startDate?.excludeDates?.map(date =>
    formatDate({
      date: date || '',
      dateFormat: SYSTEM_DATE_FORMAT_FNS,
    }),
  );

  const isIntegrationCategoryValidate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_55_STARTS_FROM,
  );

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);

  const isValidateActionStartDateInKnmAct = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_ACTION_START_DATE_IN_KNM_ACT_STARTS_FROM,
  );

  const isShow4131 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_31_STARTS_FROM,
  );

  const isShowCheckResultMessage = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.SHOW_CHECK_RESULT_MESSAGE_523_4_1_17,
  );

  const isUseEvidence = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_EVIDENCE_4_1_25_STARTS_FROM,
  );

  const isLater4111ForDecision = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.DECISION_SUBJECT_TYPE_4_1_1_START_FROM,
  );

  const isLaterThanNpaValidationKnmSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_KNM_STARTS_FROM,
  );

  const isInteractionCategoryOrInspectorRequired =
    isIntegrationCategoryValidate &&
    (!isNewKnm ||
      isKnmStatusFillInProcess ||
      isKnmStatusReadyForApprove ||
      isEditableInStatusCommentsExist ||
      (isErknmStatusRejected && isKnmPlanStatusUnderConsideration));

  const isInteractionCategoryActive =
    kindKnm?.code === KnmKindCodesEnum.MZ ||
    kindKnm?.code === KnmKindCodesEnum.KZ ||
    kindKnm?.code === KnmKindCodesEnum.IV ||
    kindKnm?.code === KnmKindCodesEnum.VP;

  const isValidateApproveNote =
    isIntegrationCategoryValidate &&
    isPlanType &&
    (approved?.id === ApprovedTypesEnum.DENIED || approved?.id === ApprovedTypesEnum.DENIED_PLANNED) &&
    (isKnmStatusApproving ||
      isEditableInStatusCommentsExist ||
      isKnmStatusTypeWaitingCarryOut ||
      isKnmStatusTypeCarriedOut);

  const isKnmKindRemarkExist =
    Array.isArray(approveRemarks) &&
    approveRemarks.some(item => item.sectionCode === ErknmApproveRemarkSectionCodeEnum.KIND_KNM);

  const shouldValidateKnmKindRemark = isKnmPlanStatusConsidered && isKnmKindRemarkExist;

  const makeKnmPlanStringValidator = (errorMsg: string) => (value: string) => {
    const valueString = value || '';

    if (valueString.length !== PLAN_ID_LENGTH || !isStringDigitsOnly(valueString)) {
      return errorMsg;
    }

    return undefined;
  };

  const makeKnmPlanFindValidator = (errorMsg: string, status: KnmStatusesEnum | undefined) => () => {
    if (status === KnmStatusesEnum.FAILED && !planGuid) {
      return errorMsg;
    }

    return undefined;
  };

  const dateWithTime = (date: string | undefined, time: string | undefined): Date | undefined => {
    const formattedDate = formatDate({
      date: date || '',
      dateFormat: DATE_FORMAT_LONG_FNS,
    });

    let formattedTime = formatDate({
      date: time || '',
      dateFormat: TIME_FORMAT,
    });

    if (!formattedTime) {
      formattedTime = formatDate({
        date: time || '',
        dateFormat: TIME_FORMAT,
        parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
      });
    }

    const dateWithTimeAsString = `${formattedDate} ${formattedTime}`;

    return parseDate({
      date: dateWithTimeAsString,
      parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
    });
  };

  const startDateTimeKnm = formatDate({
    date: dateWithTime(startDate, startTime) || '',
    dateFormat: DATE_FORMAT_ISO_FNS,
  });

  const stopDateTimeKnm = formatDate({
    date: dateWithTime(stopDate, stopTime) || '',
    dateFormat: DATE_FORMAT_ISO_FNS,
  });

  const isImpossibilityOfPrevious =
    reasonsErknm?.[0]?.reason?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;

  const getStopDateValueKnm = () => stopDate;
  const getStartDateValueKnm = () => startDate;
  const getStartTimeValueKnm = () => startDateTimeKnm;
  const getStopTimeValueKnm = () => stopDateTimeKnm;

  const getReasonsErknm = (): FormErrors<IKnmErknmFormReason>[] | undefined => {
    const isKnmReasons411Fields = isLaterThanAdminSetting(
      created,
      settings,
      AdminSettingCodesEnum.KNM_VALIDATION_4_1_1_STARTS_FROM,
    );
    const isLastConductedKnmDateRequired = isLaterThanAdminSetting(
      created,
      settings,
      AdminSettingCodesEnum.REASON_LAST_CONDUCTED_KNM_DATE_VALIDATION_4_1_54_STARTS_FROM,
    );
    const isLaterThanOrderInProd = isLaterThanAdminSetting(
      created,
      settings,
      AdminSettingCodesEnum.VALIDATION_4_1_39_STARTS_FROM,
    );

    return reasonsErknm?.map(item => {
      const getOrderValidate = (value: unknown) =>
        isAssignmentRequiredByType(item.type) && isLaterThanOrderInProd
          ? makeRequired('Поле не может быть пустым')(value)
          : undefined;

      return {
        type: makeRequired('Поле не может быть пустым')(item.type),
        text: !isNewKnm && item.type?.hasText ? makeRequired('Поле не может быть пустым')(item.text) : undefined,
        textReactQuillEditor:
          !isNewKnm && item.type?.hasText
            ? makeRequiredQuillEditorDelta('Поле не может быть пустым')(item.textReactQuillEditor)
            : undefined,
        date:
          !isNewKnm && !item.type?.assignmentNumberRequired && !item.type?.approveRequired && item.type?.dateRequired
            ? makeRequired('Поле не может быть пустым')(item.date)
            : undefined,
        riskIndicators:
          !isNewKnm && item.type?.indicatorRiskRequired
            ? getRiskIndicatorsValidate(item.riskIndicators, item.type?.indicatorRiskRequiredFile)
            : undefined,
        order: !isNewKnm ? getOrderValidate(item.order) : undefined,
        assignmentNumber: !isNewKnm && item.order ? makeRequired(REQUIRED_ERROR)(item.assignmentNumber) : undefined,
        assignmentDate: !isNewKnm && item.order ? makeRequired(REQUIRED_ERROR)(item.assignmentDate) : undefined,
        fullName: !isNewKnm && item.order ? makeRequired(REQUIRED_ERROR)(item.fullName) : undefined,
        requirementDetails:
          !isNewKnm && isKnmReasons411Fields && (item.type?.requirementDetailsRequired || item.type?.detailsRequirement)
            ? makeRequired('Поле не может быть пустым')(item.requirementDetails)
            : undefined,
        dateRequirement:
          !isNewKnm && item.type?.dateRequirement
            ? makeRequired('Поле не может быть пустым')(item.dateRequirement)
            : undefined,
        links:
          !isNewKnm && isLaterThanReasonFieldsAvailabilitySetting && isOneReasonLinkRequired(item.type)
            ? makeRequired(REQUIRED_ERROR)(item.links)
            : undefined,
        linkValidation:
          !isNewKnm && isLaterThanReasonFieldsAvailabilitySetting && isOneReasonLinkRequired(item.type)
            ? composeValidators(
                item.links?.some(link => link.linkValidationMessage)
                  ? () => item.links?.find(link => link.linkValidationMessage)?.linkValidationMessage
                  : () => undefined,
              )(item.links)
            : undefined,
        passportLinks:
          !isNewKnm && isUseEvidence && isPassportReasonLinkRequired(item.type)
            ? makeRequired(REQUIRED_ERROR)(item.passportLinks)
            : undefined,
        passportLinkValidation:
          !isNewKnm && isUseEvidence && isPassportReasonLinkRequired(item.type)
            ? composeValidators(
                item.passportLinks?.some(link => link.linkValidationMessage)
                  ? () => item.passportLinks?.find(link => link.linkValidationMessage)?.linkValidationMessage
                  : () => undefined,
              )(item.passportLinks)
            : undefined,
        violationEvidences:
          !isNewKnm && isUseEvidence && item?.passportLinks && item?.passportLinks?.length > 0
            ? getViolationEvidencesValidator(true, true)(item.violationEvidences)
            : undefined,
        lastConductedKnmDate:
          !isNewKnm && isLastConductedKnmDateRequired && item.type?.lastConductedKnmDateRequired
            ? makeRequired('Поле не может быть пустым')(item.lastConductedKnmDate)
            : undefined,
        programNumber:
          !isNewKnm &&
          isShowCheckingProgram &&
          item?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM &&
          item.type?.programNumberRequired
            ? makeRequired('Поле не может быть пустым')(item.programNumber)
            : undefined,
        programAttachment:
          !isNewKnm &&
          isShowCheckingProgram &&
          item?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM &&
          item.type?.programFileRequired
            ? makeRequired('Поле не может быть пустым')(item.programAttachment)
            : undefined,
      };
    });
  };

  const getReasonDocumentsValidate = (): FormErrors<IKnmFormDocument>[] | undefined => {
    return reasonDocuments?.map(item => ({
      type: makeRequired('Поле не может быть пустым')(item?.type),
      ...(isUseEvidence && item?.type?.code === ErknmReasonsDocumentTypesEnum.VP_VIII
        ? {
            violationEvidences: composeValidators(
              makeRequired('Поле не может быть пустым'),
              getViolationEvidencesValidator(true),
            )(item?.violationEvidences),
          }
        : {
            attachments: isValidateReasonAttachments
              ? makeRequired('Поле не может быть пустым')(item?.attachments)
              : undefined,
          }),
    }));
  };

  const getApprovedOnPaperDocumentsValidate = (): FormErrors<IKnmFormDocument> | undefined => {
    if (!values.isApprovedOnPaper) {
      return undefined;
    }

    if (!values.approvedOnPaperDocuments) {
      return {
        attachments: makeRequired('Поле не может быть пустым')(values.approvedOnPaperDocuments),
      };
    }

    return {
      attachments: makeRequired('Поле не может быть пустым')(values.approvedOnPaperDocuments.attachments),
    };
  };

  const getSubjectNotificationsValidate = (): FormErrors<boolean> | FormErrors<IKnmNotificationsForm>[] | undefined => {
    const newRescheduling = rescheduling?.find(i => i.isNew);
    const hasSubjectNotification = !!subjectNotifications?.find(i => i.isNew);

    return subjectNotifications?.map(item => {
      if (item.isNew) {
        return { date: undefined };
      }

      return {
        date:
          !newRescheduling || hasSubjectNotification
            ? undefined
            : composeValidators(
                makeNoLaterThenValidator('Внесите новые сведения об уведомлении', () => newRescheduling.newStartDate),
                makeNoSoonerThenValidator('Внесите новые сведения об уведомлении', () => newRescheduling.newEndDate),
              )(item.date),
      };
    });
  };

  const getReschedulingValidate = (): FormErrors<IKnmErknmFormRescheduling>[] | undefined => {
    return rescheduling?.map(item => ({
      newStartDate: !item.isNew
        ? undefined
        : composeValidators(
            makeNoLaterThenValidator(
              'Новая дата начала должна быть меньше или равна новой дате окончания',
              () => item?.newEndDate,
            ),
            makeRequired('Поле не может быть пустым'),
            reasonsOrderStart && !suspensionEvents?.length
              ? makeNoSoonerThenValidator(
                  'Новая дата начала должна быть больше или равна дате начала поручения',
                  () => reasonsOrderStart,
                  undefined,
                  true,
                  DATE_FORMAT_LONG_FNS,
                )
              : () => undefined,
            reasonsOrderEnd && !suspensionEvents?.length
              ? makeNoLaterThenValidator(
                  'Новая дата начала должна быть меньше или равна дате окончания поручения',
                  () => reasonsOrderEnd,
                )
              : () => undefined,
          )(item?.newStartDate),
      newEndDate: composeValidators(
        makeNoSoonerThenValidator(
          'Новая дата окончания должна быть больше или равна дате начала',
          () => item?.newStartDate,
        ),
        makeRequired('Поле не может быть пустым'),
        reasonsOrderStart && !suspensionEvents?.length
          ? makeNoSoonerThenValidator(
              'Новая дата окончания должна быть больше или равна дате начала поручения',
              () => reasonsOrderStart,
              undefined,
              true,
              DATE_FORMAT_LONG_FNS,
            )
          : () => undefined,
        reasonsOrderEnd && !suspensionEvents?.length
          ? makeNoLaterThenValidator(
              'Новая дата окончания должна быть меньше или равна дате окончания поручения',
              () => reasonsOrderEnd,
            )
          : () => undefined,
      )(item?.newEndDate),
      newDirectDurationDays: composeValidators(
        makeIsNormalInteger('Введены недопустимые символы'),
        makeMaxNumber(365, 'Число не может превышать 365'),
        !item?.newDurationHours && !item?.newDirectDurationDays
          ? makeRequired('Поле не может быть пустым')
          : () => undefined,
      )(item?.newDirectDurationDays),
      newDurationHours: composeValidators(
        makeIsNormalInteger('Введены недопустимые символы'),
        makeMaxNumber(256, 'Число не может превышать 256'),
        !item?.newDurationHours && !item?.newDirectDurationDays
          ? makeRequired('Поле не может быть пустым')
          : () => undefined,
      )(item?.newDurationHours),
    }));
  };

  const getAbrogationValidate = (): { [indesx: string]: string } | undefined => {
    if (abrogation === undefined || abrogation === null) {
      return undefined;
    }

    const obj: { [indesx: string]: string } = {};

    if (!abrogation?.reasonId?.id) {
      obj.reasonId = 'Поле не может быть пустым';
    }

    if (
      (abrogation?.reasonId?.id === 1 || abrogation?.reasonId?.id === 2) &&
      (!abrogation.link || !abrogation.pmObject?.pmData)
    ) {
      if (!abrogation.link) {
        obj.link = 'Поле не может быть пустым';
      } else if (!abrogation.pmObject?.pmData) {
        obj.link = 'Необходимо выбрать паспорт ПМ';
      }
    }

    if (abrogation.link && abrogation.pmObject?.pmData) {
      if (!abrogation.pmObject.pmData.pmErknm) {
        obj.link = 'Необходимо выбрать паспорт ПМ';
      }

      if (!obj.link && abrogation.pmObject.pmTypeValue?.code !== KnmTypeCodesEnum.PP) {
        obj.link = 'Необходимо выбрать паспорт профилактического визита';
      }

      if (!obj.link && abrogation?.reasonId?.id === 2 && !abrogation.pmObject.ervkValue?.profvizit) {
        obj.link =
          'Необходимо выбрать паспорт профилактического визита с признаком «Невозможность отказа от профилактического визита»';
      }

      if (!obj.link && abrogation.pmObject?.pmData.status?.code !== KnmErknmStatusCodesEnum.TYPE_COMPLETED) {
        obj.link = 'Необходимо выбрать паспорт профилактического визита в статусе «Завершено»';
      }

      if (
        !obj.link &&
        (kindControl?.value.dictId !== abrogation.pmObject?.pmData?.pmErknm?.supervisionType?.dictId ||
          kindControl?.value.dictVersionId !== abrogation.pmObject?.pmData?.pmErknm?.supervisionType?.dictVersionId)
      ) {
        obj.link = 'Вид контроля профилактического визита не соответствует виду контроля КНМ';
      }

      if (
        !obj.link &&
        (knoOrganizationErknm?.value.dictId !== abrogation.pmObject?.pmData?.pmErknm?.knoOrganization?.dictId ||
          knoOrganizationErknm?.value.dictVersionId !==
            abrogation.pmObject?.pmData?.pmErknm?.knoOrganization?.dictVersionId)
      ) {
        obj.link = 'КНО профилактического визита не соответствует КНО КНМ';
      }

      const isExistSubject = organizations?.find(org => {
        const orgInn = org.inn || null;
        const orgOgrn = org.ogrn || null;
        const orgSnils = org.snils || null;
        const orgRafpNza = org.rafpNza || null;

        return (
          abrogation.pmObject?.pmData?.pmErknm?.subject?.inn === orgInn &&
          abrogation.pmObject?.pmData?.pmErknm?.subject?.ogrn === orgOgrn &&
          abrogation.pmObject?.pmData?.pmErknm?.subject?.snils === orgSnils &&
          abrogation.pmObject?.pmData?.pmErknm?.subject?.rafpNza === orgRafpNza
        );
      });

      if (!obj.link && !isExistSubject) {
        obj.link =
          'Необходимо выбрать профилактический визит, контролируемое лицо которого есть в списке контролируемых лиц текущего КНМ';
      }
    }

    return obj;
  };

  function makeStartAndStopDateAdditionalValidation(
    errorMsg: string,
    fieldExcludeDates?: string[],
    minDateString?: string,
  ): (value: string) => string | undefined {
    return (value: string) => {
      const valueDate = parseDate({ date: value, parseFormat: DATE_FORMAT_LONG_FNS });
      const minDate = minDateString ? parseDate({ date: minDateString, parseFormat: DATE_FORMAT_LONG_FNS }) : undefined;
      const currentDate = new Date();
      const valueInIso = formatDate({ date: value, dateFormat: SYSTEM_DATE_FORMAT_FNS });

      const isBeforeValue = valueDate && currentDate ? isBefore(valueDate, currentDate) : undefined;

      /**
       * Основание с типом "Незамедлительная контрольная закупка", периоды неработоспособности и
       * необходимость согласования "Незамедлительное КНМ с уведомлением прокурора" проверяются на беке,
       * в зависимости от этого метод knm-excluded-dates возвращает соответствующие ограничения
       */
      if (isBeforeValue) {
        const isDateExcluded = fieldExcludeDates ? fieldExcludeDates.includes(valueInIso) : false;
        const isValidByMinDate =
          valueDate && minDate ? isAfter(valueDate, minDate) || isSameDay(valueDate, minDate) : false;

        if (isValidByMinDate && !isDateExcluded) {
          return undefined;
        }

        return errorMsg;
      }

      return undefined;
    };
  }

  function makeApprovedFieldValidation(errorMsg: string) {
    return (value: IApprovedSelectOption | undefined) => {
      if (!isErknmStatusAgreementOnApproval) {
        if (isApprovedRequired4127 && isTypeCodeVP && isApproveRequired) {
          return makeRequired(errorMsg)(value);
        }

        if (
          isProsecutor &&
          isApproveRequired &&
          !isKnmStatusFillInProcess &&
          !isKnmStatusReadyForApprove &&
          approveNote
        ) {
          return makeRequired(errorMsg)(value);
        }
      }
    };
  }

  const getOrganizationValidate = (
    item: IKnmErknmFormSubject | IKnmErknmFormOrganizationAdd,
    errorMsg: string | undefined,
    orgs: IKnmErknmFormSubject[] | IKnmErknmFormOrganizationAdd[] | undefined,
  ): FormErrors<IKnmErknmFormSubject> => {
    const isFIZ = item.subjectType?.label === KnmSubjectTypesEnum.FIZ;
    const isFIUR = item.subjectType?.label === KnmSubjectTypesEnum.FIUR;
    const isIUR = item.subjectType?.label === KnmSubjectTypesEnum.IUR;
    const isIFIZ = item.subjectType?.label === KnmSubjectTypesEnum.IFIZ;
    const isUR = item.subjectType?.label === KnmSubjectTypesEnum.UR;
    const isIP = item.subjectType?.label === KnmSubjectTypesEnum.IP;

    const subjectFnsRegistryOkvedCodes: string[] | undefined =
      fnsRegistryOkvedCodes && item.inn ? fnsRegistryOkvedCodes[item.inn]?.data : [];

    // WORK-8361 Блокируем ОКВЭД только там, где его можно отредактировать.
    const isCheckOkved =
      isShowCheckResultMessage &&
      (isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
      (item.checkSubjectResult?.egrip?.status === FnsCheckStatusesEnum.FOUND ||
        item.checkSubjectResult?.egrul?.status === FnsCheckStatusesEnum.FOUND);

    return {
      subjectType: composeValidators(makeRequired(REQUIRED_ERROR))(item.subjectType),
      ogrn:
        item.subjectType && (isIP || isUR)
          ? composeValidators(
              makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
              makeMaxCharactersValidator(32, MAX_CHARACTERS_ERROR),
              makeIsOgrn(OGRN_LENGTH_ERROR, item.subjectType?.label),
              isShow22 && item.ogrn ? isKnmSubjectExist('ogrn', item.listId, orgs, errorMsg) : () => undefined,
            )(item.ogrn)
          : undefined,
      inn: composeValidators(
        !item.rafpNza && !isIUR && !isIFIZ && !item.snils ? makeRequired(REQUIRED_ERROR) : () => undefined,
        isShow22 && item.inn ? isKnmSubjectExist('inn', item.listId, orgs, errorMsg) : () => undefined,
        item.inn ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
        item.inn
          ? makeIsErknmInn({
              errorMessage: INN_ERROR(),
              getIsFiz: () => item.isFiz,
              getIsRequired: () => kindKnm?.code !== KnmKindCodesEnum.RO,
            })
          : () => undefined,
        item.inn ? makeInnChecksumVerification() : () => undefined,
      )(item.inn),
      rafpNza: composeValidators(
        isFIUR && !item.inn && !item.kpp ? makeRequired(REQUIRED_ERROR) : () => undefined,
        isFIUR && !item.inn && !item.kpp ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
        isShow22 && isFIUR && !item.inn && !item.kpp && item.rafpNza
          ? isKnmSubjectExist('rafpNza', item.listId, orgs, errorMsg)
          : () => undefined,
        isFIUR && !item.inn && !item.kpp
          ? isDigitsCountCorrect({
              errorMessage: RAFP_NZA_ERROR,
              digitsCount: 11,
            })
          : () => undefined,
        isFIUR && !item.inn && !item.kpp ? validateRafpNzaStatus() : () => undefined,
      )(item.rafpNza),
      kpp: composeValidators(
        isFIUR && !item.rafpNza ? makeRequired(REQUIRED_ERROR) : () => undefined,
        isFIUR && !item.rafpNza ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
        isFIUR && !item.rafpNza
          ? isDigitsCountCorrect({
              errorMessage: KPP_ERROR,
              digitsCount: 9,
            })
          : () => undefined,
      )(item.kpp),
      organizationName: composeValidators(
        makeRequired(
          REQUIRED_ERROR,
          () =>
            !(
              isErknmOrganizationNameFieldEnabledWithoutFiz({
                itemIzFiz: isFIZ,
                isWatchOnly,
                isSupervisor,
                isNewKnm,
                isKnmStatusFillInProcess,
                isKnmStatusReadyForApprove,
                isKnmPlanStatusConsidered,
                knmDataApprovedType,
                knmDataNote,
              }) && isFIZ
            ) &&
            !isIFIZ &&
            !isIUR,
        ),
        makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
        isShow22 && (isIUR || isIFIZ) && item.organizationName
          ? isKnmSubjectExist('organizationName', item.listId, orgs, errorMsg)
          : () => undefined,
      )(item.organizationName),
      okveds: item.okveds?.map(okved => ({
        okved: composeValidators(
          makeRequired('Поле не может быть пустым'),
          okved.okved && isCheckOkved && !subjectFnsRegistryOkvedCodes?.includes(okved.okved.code)
            ? () => FIELD_INCORRECTLY
            : () => undefined,
        )(okved.okved),
      })),
      snils: composeValidators(
        !item.inn && isFIZ ? makeRequired(REQUIRED_ERROR) : () => undefined,
        isShow22 && item.snils ? isKnmSubjectExist('snils', item.listId, orgs, errorMsg) : () => undefined,
        makeSnilsValidation('Поле заполнено некорректно', () => !item.snils),
      )(item.snils),
      country: makeRequired(REQUIRED_ERROR, () => !isIUR && !isIFIZ)(item.country),
      document: composeValidators(
        makeRequired(REQUIRED_ERROR, () => !isIFIZ),
        makeMinCharactersValidator(3, MIN_CHARACTERS_ERROR(3), () => !isIFIZ),
        makeMaxCharactersValidator(1000, MAX_CHARACTERS_ERROR, () => !isIFIZ),
      )(item.document),
      isPresentInItRegistry:
        reasonRequirements?.it !== null &&
        ((reasonRequirements?.it && item.isPresentInItRegistry === false) ||
          (!reasonRequirements?.it && item.isPresentInItRegistry === true))
          ? CHECKING_IT_ACCREDITATION
          : undefined,
    };
  };

  const getOrganizationsValidate = (errorMsg: string): FormErrors<IKnmErknmFormSubject>[] | string | undefined => {
    const emptyActsOrganization = acts?.filter(x => x.typeAct?.id === 2)[0]?.organization;

    if (emptyActsOrganization) {
      return organizations
        ?.filter(x => x.guid !== emptyActsOrganization?.guid)
        .map(item => getOrganizationValidate(item, errorMsg, organizations));
    }

    return organizations?.map(item => getOrganizationValidate(item, errorMsg, organizations));
  };

  const getExpertsValidate = (): FormErrors<IKnmErknmFormExpert>[] | undefined => {
    return experts?.map(item => ({
      title: makeRequired('Поле не может быть пустым')(item.title),
      type: makeRequired('Поле не может быть пустым')(item.type),
    }));
  };

  const getEventsErknmValidate = (): FormErrors<IKnmErknmFormEvent>[] | undefined | string[] => {
    if (
      (shouldValidationForGeneratedDecision || isEditableInStatusCommentsExist || isImpossibilityOfPrevious) &&
      !eventsErknm?.length
    ) {
      return getArrayError('Поле не может быть пустым');
    }

    return eventsErknm?.map(item => ({
      ...getErknmEventValidate({
        item,
        isActEvent: false,
        holdingDate: undefined,
        inspectorsErknm,
        isInteractionCategoryOrInspectorRequired,
        isKnmStatusApproving,
        isKnmStatusTypeCompleted,
        isProsecutorOrSuperProsecutor,
        isShow22,
        isValidateActionStartDateInKnmAct,
        rescheduling,
        startDate,
        stopDate,
        isPm: false,
      }),
    }));
  };

  function getRequirementsErknmValidate(
    arr?: IKnmErknmFormRequirement[],
  ): FormErrors<IKnmErknmFormRequirement>[] | undefined {
    return arr?.map(item => ({
      manualTitle:
        item.requirement?.code === NEW_REQUIREMENT_SELECT_OPTION_CODE
          ? makeRequired('Поле не может быть пустым')(item.manualTitle)
          : undefined,
      manualNameNpa:
        item.requirement?.code === NEW_REQUIREMENT_SELECT_OPTION_CODE
          ? makeRequired('Поле не может быть пустым')(item.manualNameNpa)
          : undefined,
    }));
  }

  function getDocTargetValidate(): string[] | undefined {
    if (shouldValidationForGeneratedDecision && !requirementsNpaErknm?.length && !docTarget?.length) {
      return getArrayError('Поле не может быть пустым');
    }

    return undefined;
  }

  function getCollaboratingOrganizationsValidate() {
    if (!isHasCollaboratingOrganization || !isLaterThanFieldsMarksSetting) {
      return undefined;
    }

    if (!collaboratingOrganizationsErknm?.filter(Boolean).length) {
      return REQUIRED_ERROR;
    }

    return collaboratingOrganizationsErknm?.map(item => ({
      organization: !isOn411 ? makeRequired(REQUIRED_ERROR)(item?.organization) : undefined,
      title: isOn411 ? makeRequired(REQUIRED_ERROR)(item?.title) : undefined,
    }));
  }

  /**
   * Ключ обязательных требований (формулировок) связки НПА+СЕ.
   * Пустая строка означает, что формулировки не заполнены.
   */
  function getRequirementsNpaMandatoryKey(item: IKnmErknmFormRequirementNpa): string {
    return (item.mandatory ?? [])
      .map(mandatoryItem => mandatoryItem.mandatory?.id)
      .filter((id): id is number => id != null)
      .map(String)
      .sort()
      .join(',');
  }

  /**
   * Если у связки НПА+СЕ заполнены формулировки (обязательные требования),
   * уникальной считается связка НПА+СЕ+ОТ, поэтому строки с одинаковыми НПА и СЕ,
   * но разными формулировками дублями не являются.
   * Если формулировки не заполнены — уникальность проверяется по НПА+СЕ (прежнее поведение).
   */
  function isRequirementsNpaDuplicate(checkNpa: IKnmErknmFormRequirementNpa): boolean {
    const checkMandatoryKey = getRequirementsNpaMandatoryKey(checkNpa);

    return (
      (requirementsNpaErknm?.filter(
        item =>
          item.npa.id === checkNpa.npa.id &&
          item.structuralUnit.id === checkNpa.structuralUnit.id &&
          getRequirementsNpaMandatoryKey(item) === checkMandatoryKey,
      ).length || 0) > 1
    );
  }

  function getRequirementsNpaErknmValidate(): FormErrors<IKnmErknmFormRequirementNpa | string>[] | string | undefined {
    if (
      (shouldValidationForGeneratedDecision || isImpossibilityOfPrevious) &&
      !requirementsNpaErknm?.length &&
      !docTarget?.length
    ) {
      return getArrayError('Поле не может быть пустым');
    }

    let hasCorrectControlObjectFilter = true;
    let hasCorrectRiscIndicatorsFilter = true;
    let hasCorrectKnoSupervisionValidation = true;

    requirementsNpaErknm?.forEach(item => {
      if (item?.mandatory?.find(m => m?.hasInvalidControlObjectFilter)) {
        hasCorrectControlObjectFilter = false;
      }
      if (item?.mandatory?.find(m => m?.hasInvalidRiscIndicatorsFilter)) {
        hasCorrectRiscIndicatorsFilter = false;
      }

      if (item?.mandatory?.find(m => m?.hasInvalidKno || m?.hasInvalidSupervision)) {
        hasCorrectKnoSupervisionValidation = false;
      }
    });

    return requirementsNpaErknm?.map(item => {
      if (isRequirementsNpaDuplicate(item)) return { npa: 'Структурные единицы НПА не должны дублироваться' };

      if (isLaterThanNpaValidationKnmSetting) {
        const mandatoryError = makeRequired('Поле не может быть пустым')(item.mandatory);

        return {
          npa: makeRequired('')(
            hasCorrectControlObjectFilter && hasCorrectRiscIndicatorsFilter && hasCorrectKnoSupervisionValidation,
          ),
          mandatory: mandatoryError ? getArrayError(mandatoryError) : undefined,
        };
      }

      return {
        npa: undefined,
        mandatory: undefined,
      };
    });
  }

  function getPlacesValidate(): FormErrors<IKnmErknmFormPlace | string>[] | string | undefined {
    if (!isKindCodeDP) {
      if (shouldValidationForGeneratedDecision) {
        return undefined;
      }
      if (isEditableInStatusCommentsExist && !places?.length) {
        return 'Поле не может быть пустым';
      }
    }

    return places?.map(item => {
      let placesValueValidator: (value: unknown) => string | undefined = () => undefined;
      let placesManualAddressValidator = makeRequired('Поле не может быть пустым')(item.manualAddress);

      if (!item.notInFIAS) {
        if (!item.manualAddress && !item.value) {
          placesValueValidator = makeRequired('Поле не может быть пустым');
        }
        if (!item.matchesObjectAddress || item.value) {
          placesManualAddressValidator = undefined;
        }
      }

      return {
        value: composeValidators(
          isUse4123 && item.isErrorFiasValue && !item.notInFIAS ? () => NOT_IN_FIAS_TEXT : () => undefined,
          placesValueValidator,
          item.value ? makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки') : () => undefined,
        )(item.value),
        region:
          isNeedToValidatePlacesRegion && item.notInFIAS && !item.matchesObjectAddress
            ? makeRequired('Поле не может быть пустым')(item.region)
            : undefined,
        manualAddress: placesManualAddressValidator,
      };
    });
  }

  const getApproveDocsValidate = (): FormErrors<IKnmErknmFormApproveDoc>[] | undefined => {
    return approveDocs?.map(item => ({
      approveDoc: makeRequired('Поле не может быть пустым')(item.approveDoc),
    }));
  };

  const getChecklistsValidate = (): FormErrors<IKnmErknmFormChecklist | string>[] | string | undefined => {
    if (!isIntegrationCategoryValidate) {
      return undefined;
    }

    if (
      shouldValidationForGeneratedDecision &&
      (isKindCodeRO || isKindCodeVP) &&
      isTypeCodePP &&
      (!isChecklistsUsed || !checklistsErknm?.length)
    ) {
      return 'Поле не может быть пустым';
    }

    if (!isChecklistsUsed) {
      return undefined;
    }

    return checklistsErknm?.map(item => ({
      checkList: makeRequired('Поле не может быть пустым')(item.checkList),
      ervkCheckList: !isShow4119ErvkCheckLists
        ? undefined
        : makeRequired(
            'Поле не может быть пустым. При отсутствии доступных значений, заполните информацию о проверочном листе в ЕРВК',
          )(item.ervkCheckList),
      newTitle: item.checkList?.code === 'new' ? makeRequired('Поле не может быть пустым')(item.newTitle) : undefined,
      questions: getChecklistQuestionsValidate(item.questions),
    }));
  };

  const getChecklistQuestionsValidate = (
    questions: IKnmErknmFormChecklistQuestion[] | undefined,
  ): FormErrors<IKnmErknmFormChecklistQuestion>[] | string | undefined => {
    return questions?.map(item => ({
      title: makeRequired('Поле не может быть пустым')(item.title),
    }));
  };

  const getSuspensionEventsValidate = (): FormErrors<IKnmErknmFormSuspensionEvent>[] | undefined => {
    return suspensionEvents?.map(item => {
      let knmEvent: IKnmErknmFormEvent | undefined;

      if (item.event?.type?.label) {
        knmEvent = eventsErknm?.find(i => i.type?.title === item.event?.type?.label);
      }

      return {
        attachments: makeRequired('Поле не может быть пустым')(item.attachments),
        event: makeRequired('Поле не может быть пустым')(item.event),
        decisionDateTime: !isKindCodeDP ? makeRequired('Поле не может быть пустым')(item.decisionDateTime) : undefined,
        decisionNumber:
          !isEnablePaperlessDocument && !isKindCodeDP
            ? makeRequired('Поле не может быть пустым')(item.decisionNumber)
            : undefined,
        // eslint-disable-next-line no-nested-ternary
        decisionPlace:
          !isKindCodeDP && !item.notInFIAS
            ? composeValidators(
                isUse4123 && item.isErrorFiasValue && !item.notInFIAS ? () => NOT_IN_FIAS_TEXT : () => undefined,
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
              )(item.decisionPlace)
            : undefined,
        region: !isKindCodeDP && item.notInFIAS ? makeRequired('Поле не может быть пустым')(item.region) : undefined,
        manualAddress:
          !isKindCodeDP && item.notInFIAS ? makeRequired('Поле не может быть пустым')(item.manualAddress) : undefined,
        startDate: composeValidators(
          makeRequired('Поле не может быть пустым'),
          !isKindCodeDP && isEnableDateValidations
            ? makeNoSoonerThenValidator('Дата начала должна быть не менее даты издания решения о приостановке', () =>
                formatDate({
                  date: item.decisionDateTime ?? new Date().toString(),
                  dateFormat: DATE_FORMAT_LONG_FNS,
                  parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                })?.toString(),
              )
            : () => undefined,
          makeNoLaterThenValidator('Дата начала должна быть меньше или равна дате окончания', () => item.stopDate),
          makeNoSoonerThenValidator('Дата начала должна быть больше или равна дате начала КНМ', () => startDate),
          makeNoLaterThenValidator('Дата начала должна быть меньше или равна дате окончания КНМ', () => stopDate),
          // валидация чтобы дата начала приостановки не выходила за период самого действия
          makeNoSoonerThenValidator(
            'Дата начала должна быть больше или равна дате начала действия',
            () => knmEvent?.startDate,
          ),
          makeNoLaterThenValidator(
            'Дата начала должна быть меньше или равна дате окончания действия',
            () => knmEvent?.stopDate,
          ),
        )(item.startDate),
        stopDate: composeValidators(
          !isKindCodeDP ? makeRequired('Поле не может быть пустым') : () => undefined,
          item.stopDate
            ? makeNoSoonerThenValidator('Дата окончания должна быть больше или равна дате начала', () => item.startDate)
            : () => undefined,
        )(item.stopDate),
      };
    });
  };

  const validateIsDateBetweenReasonErknmDates = (date: string | undefined): string | undefined => {
    if (!date) {
      return;
    }

    return composeValidators(
      (isNewKnm || rescheduling?.length) && reasonErknmDateBegin
        ? makeNoSoonerThenValidator('КНМ должно проводиться в период действия основания', () =>
            formatDate({
              date: reasonErknmDateBegin,
              dateFormat: DATE_FORMAT_LONG_FNS,
              parseFormat: SYSTEM_DATE_FORMAT_FNS,
            }),
          )
        : () => undefined,
      (isNewKnm || rescheduling?.length) && reasonErknmDateEnd
        ? makeNoLaterThenValidator('КНМ должно проводиться в период действия основания', () =>
            formatDate({
              date: reasonErknmDateEnd,
              dateFormat: DATE_FORMAT_LONG_FNS,
              parseFormat: SYSTEM_DATE_FORMAT_FNS,
            }),
          )
        : () => undefined,
    )(date);
  };

  const reasonsOrderStart = reasonsErknm?.reduce((acc: string, item) => {
    if ((item?.order?.checkBeginDate && item?.order?.checkBeginDate > acc) || acc === undefined) {
      return item.order?.checkBeginDate;
    }

    return acc;
  }, undefined);
  const reasonsOrderEnd = reasonsErknm?.reduce((acc: string, item) => {
    if ((item?.order?.checkEndDate && item?.order?.checkEndDate < acc) || acc === undefined) {
      return item.order?.checkEndDate;
    }

    return acc;
  }, undefined);

  /*
    Вторичное согласование, если были внесены изменения в кнм, после которых нужно снова согласовать кнм.
    Но заполнения полей раздела Сведения о согласовании проведения КНМ с органами прокуратуры не должны меняться - они остаются как есть, заблокированные (валидация отключается).
    Вторичное согласование происходит в “Внесение изменений в КНМ”
   */
  const isValidateChangePlanReason =
    isProsecutorOrSuperProsecutor &&
    isKnmPlanStatusApproved &&
    isKnmStatusApproving &&
    changePlanReason?.some(
      item =>
        item?.reasonChangeType?.code === 'RSN_CHNG_PP_XII' ||
        item?.reasonChangeType?.code === 'RSN_CHNG_PP_XX' ||
        item?.reasonChangeType?.code === 'RSN_CHNG_PP_XXII',
    );

  const getChangePlanReasonValidate = (): FormErrors<IKnmErknmFormChangePlanReason>[] | undefined => {
    return changePlanReason?.map(item => {
      const isApprovedSelected = item.approvedChangePlanErknm !== undefined;

      return {
        approvedChangePlanErknm:
          isProsecutorOrSuperProsecutor && isKnmStatusApproving && isValidateChangePlanReason
            ? makeRequired('Поле не может быть пустым')(item.approvedChangePlanErknm)
            : undefined,
        approveChangePlanDateDecision: isApprovedSelected
          ? makeRequired('Поле не может быть пустым')(item.approveChangePlanDateDecision)
          : undefined,
        approveChangePlanFioSigner: isApprovedSelected
          ? makeRequired('Поле не может быть пустым')(item.approveChangePlanFioSigner)
          : undefined,
        approveChangePlanTitleSigner: isApprovedSelected
          ? makeRequired('Поле не может быть пустым')(item.approveChangePlanTitleSigner)
          : undefined,
        approveChangePlanFile: isApprovedSelected
          ? makeRequired('Поле не может быть пустым')(item.approveChangePlanFile)
          : undefined,
      };
    });
  };

  const isVideoCallInfoValidate = isShow22 && interactionCategory && interactionCategory.id !== 3 && !isCopyEnabled;

  const isRequiredSubject =
    kindKnm?.code !== KnmKindCodesEnum.RO ||
    (kindKnm?.code === KnmKindCodesEnum.RO && (!objectsErknm || objectsErknm?.length === 0));

  const resultValidateObj: FormErrors<IKnmForm> = {
    knoOrganizationErknm: makeRequired('Поле не может быть пустым')(knoOrganizationErknm),
    kindControl: makeRequired('Поле не может быть пустым')(kindControl),
    kindKnm: makeRequired('Поле не может быть пустым')(kindKnm),
    typeErknm: makeRequired('Поле не может быть пустым')(typeErknm),
    // eslint-disable-next-line no-nested-ternary
    placeDecision: isUse4123
      ? composeValidators(
          isErrorFiasValue && !isNotInFIAS
            ? () => NOT_IN_FIAS_TEXT
            : shouldValidationForGeneratedDecision && !isNotInFIAS && !isDecisionDisabled
            ? () => makeRequired('Поле не может быть пустым')(placeDecision)
            : () => undefined,
          makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
        )(placeDecision)
      : shouldValidationForGeneratedDecision
      ? makeRequired('Поле не может быть пустым')(placeDecision)
      : undefined,
    region: isNotInFIAS ? makeRequired('Поле не может быть пустым')(region) : undefined,
    manualAddress: isNotInFIAS ? makeRequired('Поле не может быть пустым')(manualAddress) : undefined,
    fioSigner: shouldValidationForGeneratedDecision ? makeRequired('Поле не может быть пустым')(fioSigner) : undefined,
    titleSigner: composeValidators(
      shouldValidationForGeneratedDecision ? makeRequired('Поле не может быть пустым') : () => undefined,
    )(titleSigner),
    planId:
      isPlanType && !isNewKnm && !isStartDateIsCurrentYear
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeKnmPlanStringValidator('Номер плана должен содержать 10 цифр'),
            makeKnmPlanFindValidator('Номер плана не найден', planFindStatus),
          )(planId)
        : undefined,
    planSelectOption:
      isPlanType && !isNewKnm && isStartDateIsCurrentYear
        ? composeValidators(makeRequired('Поле не может быть пустым'))(planSelectOption)
        : undefined,
    oldPlanNumber: isTypeCodePP248P294
      ? composeValidators(
          makeIsNormalInteger('Введены недопустимые символы'),
          makeRequired('Поле не может быть пустым'),
        )(oldPlanNumber)
      : undefined,
    oldKnmNumber: isTypeCodePP248P294
      ? composeValidators(
          makeIsNormalInteger('Введены недопустимые символы'),
          makeRequired('Поле не может быть пустым'),
        )(oldKnmNumber)
      : undefined,
    startDate:
      composeValidators(
        makeNoLaterThenValidator('Дата начала должна быть меньше или равна дате окончания', getStopDateValueKnm),
        makeRequired('Поле не может быть пустым'),
        isTypeCodePP &&
          dateIn30WorkingDays &&
          isStartDateIsCurrentYear &&
          (isKnmStatusFillInProcess || isKnmStatusReadyForApprove || isNewKnm)
          ? makeNoSoonerThenValidator('До даты начала КНМ должно быть более 30 рабочих дней', () => dateIn30WorkingDays)
          : () => undefined,
        planSelectOption?.value === newPlanSelectOption.value && dateIn30WorkingDays
          ? makeNoSoonerThenValidator('До даты начала КНМ должно быть более 30 рабочих дней', () => dateIn30WorkingDays)
          : () => undefined,
        reasonsOrderStart && !suspensionEvents?.length
          ? makeNoSoonerThenValidator(
              'Дата начала должна быть больше или равна дате начала поручения',
              () => reasonsOrderStart,
              undefined,
              true,
              DATE_FORMAT_LONG_FNS,
            )
          : () => undefined,
        reasonsOrderEnd && !suspensionEvents?.length
          ? makeNoLaterThenValidator(
              'Дата начала должна быть меньше или равна дате окончания поручения',
              () => reasonsOrderEnd,
            )
          : () => undefined,
        isTypeCodeVP && isNewKnm
          ? makeStartAndStopDateAdditionalValidation(
              'Дата не может быть меньше, чем текущая дата',
              startExcludedDates,
              excludeDates?.startDate?.minDate,
            )
          : () => undefined,
      )(startDate) ||
      (isNewKnm && isUseKnoSupervisorStartDate
        ? composeValidators(
            makeRequired(
              'Отсутствуют сведения о дате начала действия вида контроля. Необходимо внести сведения в ЕРВК.',
            ),
            makeNoLaterThenValidator(
              'Выбранная дата раньше начала вступления в силу положения о виде контроля (надзора)',
              () => startDate,
            ),
          )(knoSupervisionStartDate)
        : undefined) ||
      validateIsDateBetweenReasonErknmDates(startDate),
    startTime:
      (isNewKnm || isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
      isShow4131 &&
      isTypeCodeVP &&
      isApproveRequired
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeNoLaterThenValidator(
              'Время начала мероприятия должно быть меньше времени окончания',
              getStopTimeValueKnm,
              false,
            ),
          )(startDateTimeKnm)
        : () => undefined,
    stopDate: composeValidators(
      makeNoSoonerThenValidator('Дата окончания должна быть больше или равна дате начала', getStartDateValueKnm),
      isKnmMandatoryDates ? makeRequired('Поле не может быть пустым') : () => undefined,
      reasonsOrderStart && !suspensionEvents?.length
        ? makeNoSoonerThenValidator(
            'Дата окончания должна быть больше или равна дате начала поручения',
            () => reasonsOrderStart,
            undefined,
            true,
            DATE_FORMAT_LONG_FNS,
          )
        : () => undefined,
      reasonsOrderEnd && !suspensionEvents?.length
        ? makeNoLaterThenValidator(
            'Дата окончания должна быть меньше или равна дате окончания поручения',
            () => reasonsOrderEnd,
          )
        : () => undefined,
      validateIsDateBetweenReasonErknmDates,
      isTypeCodeVP && isNewKnm
        ? makeNoSoonerThenValidator('Дата не может быть меньше, чем текущая дата', () =>
            max(
              [
                parseDate({
                  date: excludeDates?.stopDate.minDate ?? new Date().toString(),
                  parseFormat: SYSTEM_DATE_FORMAT_FNS,
                }),
                parseDate({
                  date: startDate ?? new Date().toString(),
                  parseFormat: DATE_FORMAT_LONG_FNS,
                }),
              ].filter(Boolean) as Date[],
            ).toString(),
          )
        : () => undefined,
    )(stopDate),
    stopTime:
      (isNewKnm || isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
      isShow4131 &&
      isTypeCodeVP &&
      isApproveRequired
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeNoSoonerThenValidator(
              'Время окончания мероприятия должно быть больше времени начала',
              getStartTimeValueKnm,
              undefined,
              false,
            ),
          )(stopDateTimeKnm)
        : () => undefined,
    durationDays: composeValidators(
      makeIsNormalInteger('Введены недопустимые символы'),
      makeMaxNumber(365, 'Число не может превышать 365'),
      isKnmMandatoryDates && !isNewKnm ? makeRequired('Поле не может быть пустым') : () => undefined,
    )(durationDays),
    durationHours: composeValidators(
      makeIsNormalInteger('Введены недопустимые символы'),
      makeMaxNumber(256, 'Число не может превышать 256'),
      isKnmMandatoryDates ? makeIsNotEqualNumber(0, 'Введено некорректное значение') : () => undefined,
      isKnmMandatoryDates && !directDurationDays && !isKindCodeDP && !isNewKnm
        ? makeRequired('Поле не может быть пустым')
        : () => undefined,
    )(durationHours),
    abrogation: getAbrogationValidate(),
    rescheduling: getReschedulingValidate(),
    subjectNotifications: getSubjectNotificationsValidate(),
    directDurationDays: composeValidators(
      makeIsNormalInteger('Введены недопустимые символы'),
      makeMaxNumber(365, 'Число не может превышать 365'),
      isKnmMandatoryDates ? makeIsNotEqualNumber(0, 'Введено некорректное значение') : () => undefined,
      isKnmMandatoryDates && !durationHours && !isKindCodeDP && !isNewKnm
        ? makeRequired('Поле не может быть пустым')
        : () => undefined,
    )(directDurationDays),
    approveRequired:
      isReasonsNotEmpty && isTypeCodeVP ? makeRequired('Поле не может быть пустым')(approveRequired) : undefined,
    reasonsErknm: isUnscheduledType ? getReasonsErknm() : undefined,
    reasonDocuments:
      (approveRequired?.value === ApproveRequiredTypesEnum.PROMPTLY ||
        approveRequired?.value === ApproveRequiredTypesEnum.REQUIRED) &&
      isTypeCodeVP
        ? getReasonDocumentsValidate()
        : undefined,
    prosecutorOrganizationErknm: makeRequired('Поле не может быть пустым')(prosecutorOrganizationErknm),
    approveNote: isValidateApproveNote
      ? composeValidators(
          makeRequired('Поле не может быть пустым'),
          makeMaxCharactersValidator(4096, 'Превышена допустимая длина строки'),
        )(approveNote)
      : undefined,
    approved: composeValidators(
      makeApprovedFieldValidation(REQUIRED_ERROR),
      isProsecutorOrSuperProsecutor &&
        (isKnmStatusApproving || isKnmStatusCommentsExist) &&
        approved?.value === ApprovedTypesEnum.APPROVED_WITH_REMARK &&
        !approveRemarks?.length
        ? () => 'В паспорте должно быть указано хотя бы одно замечание'
        : () => undefined,
      isProsecutorOrSuperProsecutor &&
        isKnmStatusApproving &&
        approved?.value !== ApprovedTypesEnum.APPROVED_WITH_REMARK &&
        !!approveRemarks?.filter(item => !item.changes?.length).length
        ? () => 'При наличии замечаний значение должно быть “Согласовано с замечанием”'
        : () => undefined,
      (value: IApprovedSelectOption | undefined) =>
        isProsecutorOrSuperProsecutor &&
        isKnmStatusApproving &&
        isLaterThanFieldsMarksSetting &&
        value?.value === ApprovedTypesEnum.ON_APPROVAL &&
        isValidateChangePlanReason
          ? 'Необходимо внести решение'
          : undefined,
    )(approved),
    approveNumberDecision:
      isApprovedRequired4127 &&
      isApproveRequired &&
      isProsecutorOrSuperProsecutor &&
      isKnmStatusApproving &&
      !isValidateChangePlanReason &&
      approved?.value !== ApprovedTypesEnum.ON_APPROVAL
        ? makeRequired('Поле не может быть пустым')(approveNumberDecision)
        : undefined,
    approveFioSigner:
      isApprovedRequired4127 &&
      isProsecutorOrSuperProsecutor &&
      isKnmStatusApproving &&
      !isValidateChangePlanReason &&
      approved?.value !== ApprovedTypesEnum.ON_APPROVAL
        ? makeRequired('Поле не может быть пустым')(approveFioSigner)
        : undefined,
    approveTitleSigner:
      isApprovedRequired4127 &&
      isApproveRequired &&
      isProsecutorOrSuperProsecutor &&
      isKnmStatusApproving &&
      !isValidateChangePlanReason &&
      approved?.value !== ApprovedTypesEnum.ON_APPROVAL
        ? makeRequired('Поле не может быть пустым')(approveTitleSigner)
        : undefined,
    approveDateDecision:
      isApprovedRequired4127 &&
      isApproveRequired &&
      isProsecutorOrSuperProsecutor &&
      isKnmStatusApproving &&
      !isValidateChangePlanReason &&
      approved?.value !== ApprovedTypesEnum.ON_APPROVAL &&
      !isErknmStatusAgreementOnApproval
        ? makeRequired('Поле не может быть пустым')(approveDateDecision)
        : undefined,
    approveNoteAppeal: approveNoteAppeal
      ? makeMaxCharactersValidator(4096, 'Превышена допустимая длина строки')(approveNoteAppeal)
      : undefined,
    rejectReasonType:
      isIntegrationCategoryValidate && approved?.value === ApprovedTypesEnum.DENIED && !isPlanType
        ? makeRequired('Поле не может быть пустым')(rejectReasonType)
        : undefined,
    approveFile:
      !webDocuments?.length &&
      isApprovedRequired4127 &&
      isProsecutorOrSuperProsecutor &&
      isKnmStatusApproving &&
      !isValidateChangePlanReason &&
      approved?.value !== ApprovedTypesEnum.ON_APPROVAL
        ? makeRequired('Поле не может быть пустым')(approveFile)
        : undefined,
    organizations: getOrganizationsValidate(KNM_SUBJECT_IS_EXIST_ERROR),
    objectsErknm: getObjectsErknmValidate(
      objectsErknm,
      isEditableInStatusCommentsExist,
      shouldValidationForGeneratedDecision || shouldValidateKnmKindRemark,
      isUse4123,
      isUse2234114,
      isTypeCodePP,
      new Date().getFullYear() ===
        parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS })?.getFullYear(),
      isLaterThanFieldsMarksSetting,
      isHazardClassApplies,
      isImpossibilityOfPrevious,
    ),
    inspectorsErknm: getInspectorsErknmValidate(
      inspectorsErknm,
      isEditableInStatusCommentsExist,
      shouldValidationForGeneratedDecision,
      isTypeCodeVP,
    ),
    experts: experts && experts.length ? getExpertsValidate() : undefined,
    remoteNote:
      !isIntegrationCategoryValidate && isRemote?.value
        ? makeRequired('Поле не может быть пустым')(remoteNote)
        : undefined,
    interactionCategory:
      isInteractionCategoryOrInspectorRequired && isInteractionCategoryActive
        ? makeRequired('Поле не может быть пустым')(interactionCategory)
        : undefined,
    videoCallInfo: isVideoCallInfoValidate
      ? composeValidators(
          isRequiredSubject ? makeRequired('Поле не может быть пустым') : () => undefined,
          videoCallInfoValidator(rescheduling, startDate, stopDate),
        )(videoCallInfo)
      : () => undefined,
    eventsErknm: getEventsErknmValidate(),
    requirementsErknm: requirementsErknm?.length ? getRequirementsErknmValidate(requirementsErknm) : undefined,
    requirementsNpaErknm: getRequirementsNpaErknmValidate(),
    approveDocs: !isShow22 && approveDocs?.length ? getApproveDocsValidate() : undefined,
    places: getPlacesValidate(),
    organizationDocuments: organizationDocuments?.map(item => ({
      value: makeRequired('Поле не может быть пустым')(item.value),
    })),
    checklistsErknm: getChecklistsValidate(),
    acts: getErknmActsValidate({
      acts,
      getOrganizationValidate,
      isInteractionCategoryOrInspectorRequired,
      inspectorsErknm,
      isKnmStatusApproving,
      isKnmStatusTypeCompleted,
      isProsecutorOrSuperProsecutor,
      isShow22,
      isValidateActionStartDateInKnmAct,
      rescheduling,
      startDate,
      stopDate,
      isKnmStatusTypeCarriedOut,
      isLaterThanFieldsMarksSetting,
      isShow4127,
      isValidateRequiredViolatedMandatory,
      isViolationsStartDate,
      kindKnm,
      organizations,
      isKindCodeRO,
      eventsErknm,
      isEnablePaperlessDocument,
      // Блок повторного нарушения и требование его полей должны включаться вместе. Иначе на паспорте,
      // где исходное КНМ создано по старым правилам, валидатор просит repeatCorrected,
      // а поля на форме нет.
      isRepeatValidationChecking:
        isEnablePrescriptionCheck &&
        (reasonsErknm?.[0].type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK ||
          isPassportByCheckPrescriptionFailed === true) &&
        isPassportByCheckPrescriptionOldRules === false,
    }),
    violatedRequirements: getRequirementsErknmValidate(violatedRequirements),
    resultDecisions: getErknmResultDecisionsValidate({
      resultDecisions,
      getOrganizationValidate,
      isShow4127,
      punishmentNature,
      kindKnm,
      isKnmStatusFillInProcess,
      isIntegrationCategoryValidate,
      isValidateRequiredViolatedMandatory,
      isViolationsStartDate,
      subjectTypeOptions,
      eventsErknm,
      acts,
      organizations,
      fnsRegistryOkvedCodes,
      isShowCheckResultMessage,
      isKnmStatusTypeCarriedOut,
      isWatchOnly,
      isSupervisor,
      isNewKnm,
      isKnmStatusReadyForApprove,
      isKnmPlanStatusConsidered,
      knmDataApprovedType,
      knmDataNote,
      isLater4111ForDecision,
    }),
    judgments: getJudgmentsValidate(judgments),
    documents: getDocumentsValidate(documents, isIntegrationCategoryValidate),
    suspensionEvents: getSuspensionEventsValidate(),
    docTarget: getDocTargetValidate(),
    collaboratingOrganizationsErknm: getCollaboratingOrganizationsValidate(),
    agreements: agreements?.map(agreement =>
      getErknmAgreementValidate({
        agreement,
        isPaperless: !!resultDecisions?.find(i => i.guid === agreement?.guid)?.paperless,
        isSupervisor: !!isSupervisor,
      }),
    ),
    approvedOnPaperDocuments: getApprovedOnPaperDocumentsValidate(),
    changePlanReason: getChangePlanReasonValidate(),
  };

  const invalidFieldsValidate: FormErrors<IKnmForm> = validateInvalidFields(
    values,
    initialErknmValues,
    isNewKnm,
    isKnmStatusTypeCarriedOut,
    isKnmStatusTypeCompleted,
    created,
    settings,
  );
  const resultValidate = cloneFormErrors(mergeDeepRightWithArray(resultValidateObj, invalidFieldsValidate));

  if ((isEditableInStatusCommentsExist || isKnmStatusTypeCarriedOut) && !inspectorsErknm?.length) {
    resultValidate['inspectorsErknm' as keyof IKnmForm] = getArrayError(REQUIRED_ERROR);
  }

  if (resultValidate['agreements' as keyof IKnmForm]) {
    resultValidate['agreements' as keyof IKnmForm].forEach((agreement: IErknmFormAgreement, index: number) => {
      if (Array.isArray(agreement?.terminate?.terminationReasons)) {
        let isReasonsEmpty = true;

        agreement?.terminate?.terminationReasons?.forEach(reason => {
          if (!R.isNil(reason)) {
            isReasonsEmpty = false;
          }
        });

        if (agreement?.terminate && isReasonsEmpty) {
          resultValidate['agreements' as keyof IKnmForm][index].terminate.terminationReasons = undefined;
        }
      }

      if (Array.isArray(agreement?.changes)) {
        agreement?.changes?.forEach((change, changeIndex) => {
          let isReasonsEmpty = true;

          change.types?.forEach(type => {
            if (!R.isNil(type)) {
              isReasonsEmpty = false;
            }
          });

          if (isReasonsEmpty) {
            resultValidate['agreements' as keyof IKnmForm][index].changes[changeIndex].types = undefined;
          }

          isReasonsEmpty = true;
          change.reasons?.forEach(reason => {
            if (!R.isNil(reason)) {
              isReasonsEmpty = false;
            }
          });

          if (isReasonsEmpty) {
            resultValidate['agreements' as keyof IKnmForm][index].changes[changeIndex].reasons = undefined;
          }
        });
      }
    });
  }

  if (resultValidate['resultDecisions' as keyof IKnmForm]) {
    resultValidate['resultDecisions' as keyof IKnmForm].forEach(
      (resultDecision: IKnmErknmFormResultDecision, index: number) => {
        if (Array.isArray(resultDecision?.inspectors)) {
          let isInspectorsEmpty = true;

          resultDecision?.inspectors?.forEach(inspector => {
            if (!R.isEmpty(inspector)) {
              isInspectorsEmpty = false;
            }
          });

          if (resultDecision && isInspectorsEmpty) {
            resultValidate['resultDecisions' as keyof IKnmForm][index].inspectors = undefined;
          }
        }
      },
    );
  }

  if (resultValidate['requirementsNpaErknm' as keyof IKnmForm]) {
    resultValidate['requirementsNpaErknm' as keyof IKnmForm].forEach(
      (_requirementNpa: FormErrors<IKnmErknmFormRequirementNpa>, index: number) => {
        if (!requirementsNpaErknm?.[index]?.mandatory?.length) {
          return;
        }

        const requirementsNpaErrors = resultValidate['requirementsNpaErknm' as keyof IKnmForm][index];

        if (typeof requirementsNpaErrors?.mandatory === 'string') {
          resultValidate['requirementsNpaErknm' as keyof IKnmForm][index].mandatory = undefined;
        }
      },
    );
  }

  (['judgmentAgainstOfficialPerson', 'cancellationActsAndDecisions'] as const).forEach(key =>
    wrapArrayError(resultValidate, key),
  );

  if (
    isHasCollaboratingOrganization &&
    isLaterThanFieldsMarksSetting &&
    !collaboratingOrganizationsErknm?.filter(Boolean).length
  ) {
    resultValidate['collaboratingOrganizationsErknm' as keyof IKnmForm] = getArrayError(REQUIRED_ERROR);
  } else {
    wrapArrayError(resultValidate, 'collaboratingOrganizationsErknm');
  }

  wrapNestedArrayError(resultValidate['judgments' as keyof IKnmForm], 'judgmentSubjectData');
  wrapNestedArrayError(resultValidate['reasonsErknm' as keyof IKnmForm], 'riskIndicators');
  wrapNestedArrayError(resultValidate['checklistsErknm' as keyof IKnmForm], 'questions');

  return resultValidate;
};
